/* ============================================
   NODUS HOME · VOICES
   Paged testimonial carousel. Page size is measured from the rendered
   layout, so it follows the CSS breakpoints without duplicating them here.
   ============================================ */

if (!customElements.get('nodus-home-voices')) {
	class NodusHomeVoices extends HTMLElement {
		connectedCallback() {
			this.track = this.querySelector('[data-nodus-voices-track]');

			if (!this.track) return;

			this.dotsHolder = this.querySelector('[data-nodus-voices-dots]');
			this.prevButton = this.querySelector('[data-nodus-voices-prev]');
			this.nextButton = this.querySelector('[data-nodus-voices-next]');
			this.nav = this.querySelector('.nodus-home-voices__nav');

			this.page = 0;
			this.timer = null;
			this.scrollFrame = null;
			this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

			this.handlePrev = () => this.goTo(this.page - 1, true);
			this.handleNext = () => this.goTo(this.page + 1, true);
			this.handleDotClick = this.onDotClick.bind(this);
			this.handleScroll = this.onScroll.bind(this);
			this.handleResize = this.onResize.bind(this);
			this.handlePause = () => this.stopAutoplay();
			this.handleResume = () => this.startAutoplay();

			if (this.prevButton) this.prevButton.addEventListener('click', this.handlePrev);

			if (this.nextButton) this.nextButton.addEventListener('click', this.handleNext);

			if (this.dotsHolder) this.dotsHolder.addEventListener('click', this.handleDotClick);

			this.track.addEventListener('scroll', this.handleScroll, { passive: true });
			window.addEventListener('resize', this.handleResize);
			this.addEventListener('mouseenter', this.handlePause);
			this.addEventListener('mouseleave', this.handleResume);
			this.addEventListener('focusin', this.handlePause);
			this.addEventListener('focusout', this.handleResume);

			this.buildDots();
			this.startAutoplay();
		}

		disconnectedCallback() {
			this.stopAutoplay();

			if (this.scrollFrame) {
				window.cancelAnimationFrame(this.scrollFrame);
				this.scrollFrame = null;
			}

			if (this.prevButton && this.handlePrev) this.prevButton.removeEventListener('click', this.handlePrev);

			if (this.nextButton && this.handleNext) this.nextButton.removeEventListener('click', this.handleNext);

			if (this.dotsHolder && this.handleDotClick) {
				this.dotsHolder.removeEventListener('click', this.handleDotClick);
			}

			if (this.track && this.handleScroll) this.track.removeEventListener('scroll', this.handleScroll);

			if (this.handleResize) window.removeEventListener('resize', this.handleResize);

			this.removeEventListener('mouseenter', this.handlePause);
			this.removeEventListener('mouseleave', this.handleResume);
			this.removeEventListener('focusin', this.handlePause);
			this.removeEventListener('focusout', this.handleResume);
		}

		get items() {
			return Array.from(this.track.children);
		}

		/* Distance between two consecutive slides: slide width plus the grid gap. */
		get step() {
			const items = this.items;

			if (items.length < 2) return this.track.clientWidth;

			return items[1].offsetLeft - items[0].offsetLeft;
		}

		get perView() {
			const step = this.step;

			if (step <= 0) return 1;

			return Math.max(1, Math.round(this.track.clientWidth / step));
		}

		get pageCount() {
			return Math.max(1, Math.ceil(this.items.length / this.perView));
		}

		buildDots() {
			if (this.nav) this.nav.hidden = this.pageCount < 2;

			if (!this.dotsHolder) return;

			const total = this.pageCount;
			const template = this.dotsHolder.dataset.dotLabel || '%d';

			this.dotsHolder.textContent = '';

			if (total < 2) return;

			for (let index = 0; index < total; index += 1) {
				const dot = document.createElement('button');

				dot.type = 'button';
				dot.className = 'nodus-home-voices__dot';
				dot.dataset.page = String(index);
				dot.setAttribute('aria-label', template.replace('%d', String(index + 1)));
				this.dotsHolder.appendChild(dot);
			}

			this.syncDots();
		}

		syncDots() {
			if (!this.dotsHolder) return;

			for (const dot of this.dotsHolder.children) {
				const isActive = Number(dot.dataset.page) === this.page;

				dot.classList.toggle('is-active', isActive);
				dot.setAttribute('aria-current', isActive ? 'true' : 'false');
			}
		}

		goTo(page, restart) {
			const items = this.items;
			const total = this.pageCount;

			if (items.length === 0) return;

			this.page = ((page % total) + total) % total;

			const index = Math.min(this.page * this.perView, items.length - 1);

			this.track.scrollTo({
				left: items[index].offsetLeft - items[0].offsetLeft,
				behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
			});
			this.syncDots();

			if (restart) this.startAutoplay();
		}

		onDotClick(event) {
			const dot = event.target.closest('[data-page]');

			if (dot) this.goTo(Number(dot.dataset.page), true);
		}

		onScroll() {
			if (this.scrollFrame) return;

			this.scrollFrame = window.requestAnimationFrame(() => {
				this.scrollFrame = null;

				const step = this.step;

				if (step <= 0) return;

				const slide = Math.round(this.track.scrollLeft / step);
				const page = Math.min(Math.round(slide / this.perView), this.pageCount - 1);

				if (page !== this.page) {
					this.page = page;
					this.syncDots();
				}
			});
		}

		onResize() {
			this.buildDots();
			this.goTo(0, false);
		}

		startAutoplay() {
			this.stopAutoplay();

			if (this.dataset.autoplay !== 'true' || this.reducedMotion.matches) return;

			if (this.pageCount < 2) return;

			const delay = (Number(this.dataset.autoplayDelay) || 8) * 1000;

			this.timer = window.setInterval(() => this.goTo(this.page + 1, false), delay);
		}

		stopAutoplay() {
			if (this.timer) {
				window.clearInterval(this.timer);
				this.timer = null;
			}
		}
	}

	customElements.define('nodus-home-voices', NodusHomeVoices);
}
